import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function ComparisonBarChart({ 
  data = [], 
  categories,
  dataKeys = [],
  xKey = 'category',
  xLabel, 
  yLabel,
  title,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
  horizontal = false
}) {
  // Validate data
  if (!data || data.length === 0 || !dataKeys || dataKeys.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>No data available for chart</p>
      </div>
    );
  }
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
          <p className="text-slate-100 font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-slate-300 text-sm">
              <span className="font-semibold" style={{ color: entry.color }}>
                {entry.name}:
              </span> {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ChartComponent = horizontal ? BarChart : BarChart;
  const chartProps = horizontal ? {
    layout: "horizontal",
    margin: { top: 20, right: 30, bottom: 20, left: 100 }
  } : {
    layout: "vertical",
    margin: { top: 20, right: 30, bottom: 60, left: 40 }
  };

  return (
    <div className="w-full h-full">
      {title && (
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{title}</h3>
      )}
      
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={data} {...chartProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          
          {horizontal ? (
            <>
              <XAxis 
                type="number"
                label={{ value: yLabel, position: 'insideBottom', offset: -10, fill: '#94a3b8' }}
                stroke="#64748b"
                tick={{ fill: '#94a3b8' }}
              />
              <YAxis 
                type="category"
                dataKey={xKey}
                label={{ value: xLabel, angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
                stroke="#64748b"
                tick={{ fill: '#94a3b8' }}
                width={90}
              />
            </>
          ) : (
            <>
              <XAxis 
                dataKey={xKey}
                label={{ value: xLabel, position: 'insideBottom', offset: -10, fill: '#94a3b8' }}
                stroke="#64748b"
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
                stroke="#64748b"
                tick={{ fill: '#94a3b8' }}
              />
            </>
          )}
          
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="square"
          />
          
          {dataKeys.map((key, index) => (
            <Bar 
              key={key}
              dataKey={key}
              fill={colors[index % colors.length]}
              name={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}
